/**
 * Vorwahl an der Kasse: ein Wert, der VOR der Artikelwahl eingetippt wird (Touch-Ziffernblock
 * oder physische Tastatur) und genau für den nächsten Artikel bzw. Scan gilt.
 *
 * - Stückartikel: ganze Zahl 1 … {@link MAX_VORGABE_MENGE} = Menge.
 * - Wiegeartikel: Kilogramm mit höchstens drei Nachkommastellen (1 g … 99,999 kg, dieselbe
 *   Grenze wie der Gewichtsdialog: `GEWICHT_MAX_GRAMM`). Die Position entsteht über
 *   `waehleArtikel` mit einer {@link Gewichtsvorgabe} der Herkunft `'vorwahl'` und damit über
 *   `positionAusGewicht`/`gewichtPreisCent` – es gibt hier keine eigene Preisrechnung.
 * - Ungültige Eingaben werden nie still in einer anderen Einheit gedeutet: Ergebnis
 *   „abgewiesen“ mit Meldung, die Vorwahl bleibt stehen, der Artikel wird nicht übernommen.
 *
 * Beträge laufen NICHT über die Vorwahl, sondern weiter über die freie Position
 * (`mengenfeld.ts` `freiePosition`); eine Preisüberschreibung je Artikel gibt es nicht.
 *
 * Rein und ohne React; die Oberfläche (Vorwahl V1, freigegeben 2026-11-04) hängt sich nur ein,
 * siehe docs/design-entwuerfe/vorwahl-logik.md und `komponenten/Vorwahl.tsx`.
 */
import {
  formatiereKg,
  GEWICHT_MAX_GRAMM,
  grammAusEingabe,
  verkaufsartVon,
  waehleArtikel,
  type Auswahl,
  type Verkaufsart,
  type WiegeAnfrage,
} from './gewichtsverkauf';
import { MAX_VORGABE_MENGE, positionMitMenge, type Schnellwahl } from './schnellwahl';
import type { Position } from './warenkorb';

export { MAX_VORGABE_MENGE };

// ---------------------------------------------------------------------------------------------
// Eingabe
// ---------------------------------------------------------------------------------------------

/** Taste der Vorwahl: Ziffer, Dezimaltrenner (Komma; Punkt wird zu Komma), ⌫, Löschen. */
export type VorwahlTaste =
  '0' | '1' | '2' | '3' | '4' | '5' | '6' | '7' | '8' | '9' | ',' | '⌫' | 'C';

/**
 * Längste gültige Vorwahl in Zeichen. Bewusst großzügiger als die gültigen Werte, damit zu große
 * Mengen bzw. zu viele Nachkommastellen ausdrücklich abgewiesen werden (Meldung) statt still
 * am Tippen gehindert. Längere Werte sind ein Überlauf und werden immer abgewiesen.
 */
export const VORWAHL_MAX_ZEICHEN = 8;

/** Dezimaltrenner der Vorwahl nach dem Normalisieren. */
const TRENNER = ',';

/**
 * Normalisiert eine Vorwahl: Leerraum am Rand weg, Punkt wird Komma (`,` und `.` sind
 * gleichwertig). Sonst unverändert – geprüft wird erst bei der Auswertung.
 */
export function normalisiereVorwahl(text: string): string {
  return text.trim().replaceAll('.', TRENNER);
}

/**
 * Vorwahl nach einer Taste. Ziffern werden angehängt (aus „0“ wird beim nächsten Ziffern-
 * druck die Ziffer), ein Trenner am Anfang ergibt „0,“, ein zweiter Trenner macht den Wert ungültig; ⌫ löscht
 * das letzte Zeichen, C alles.
 *
 * Überlauf: Eine Taste wird nie still verschluckt, solange der Wert dadurch gültig, aber falsch
 * bliebe („00000001“ + „0“ darf nicht 1 Stück bleiben). Über {@link VORWAHL_MAX_ZEICHEN} hinaus
 * wird deshalb noch genau ein Zeichen angenommen – der Wert ist dann zu lang und wird bei der
 * Auswertung abgewiesen; weitere Tasten ändern an diesem ungültigen Wert nichts mehr.
 */
export function vorwahlTaste(text: string, taste: VorwahlTaste): string {
  const t = normalisiereVorwahl(text);
  if (taste === 'C') {
    return '';
  }
  if (taste === '⌫') {
    return t.slice(0, -1);
  }
  if (t.length > VORWAHL_MAX_ZEICHEN) {
    return t;
  }
  if (taste === TRENNER) {
    // ein zweiter Trenner wird nicht verschluckt („1,2“ + „,“ + „5“ wäre sonst still 1,25):
    // er macht den Wert ungültig, die Auswertung weist ihn ab
    return t === '' ? `0${TRENNER}` : `${t}${TRENNER}`;
  }
  return t === '0' ? taste : `${t}${taste}`;
}

/** Zeichen → Vorwahltaste (`.` gilt als `,`); sonst null. */
export function vorwahlTasteAusZeichen(zeichen: string): VorwahlTaste | null {
  if (/^[0-9]$/.test(zeichen)) {
    return zeichen as VorwahlTaste;
  }
  if (zeichen === ',' || zeichen === '.') {
    return TRENNER;
  }
  return null;
}

// ---------------------------------------------------------------------------------------------
// Auswertung
// ---------------------------------------------------------------------------------------------

/** Meldungen der Auswertung (gleichlautend für Oberfläche und Tests). */
export const VORWAHL_MELDUNG = {
  stueckNurGanz: 'Stückartikel – nur ganze Zahlen',
  stueckMindestens: 'Stückartikel – Menge mindestens 1',
  stueckHoechstens: `Stückartikel – höchstens ${String(MAX_VORGABE_MENGE)} Stück`,
  ungueltig: 'Ungültige Eingabe – nur Ziffern und ein Komma',
  zuLang: 'Eingabe zu lang – bitte löschen und neu eingeben',
  nichtUebernommen: 'Artikel nicht übernommen – Vorwahl bleibt',
  mengeUngueltig: 'Ungültige Menge',
  negativ: 'Keine negativen Werte',
  gewichtFehlt: 'Gewicht fehlt – Ziffern eingeben',
  gewichtNull: 'Gewicht muss größer als 0 kg sein',
  gewichtNachkomma: 'Gewicht in kg – höchstens 3 Nachkommastellen',
  gewichtHoechstens: 'Gewicht höchstens 99,999 kg',
} as const;

/** Ergebnis der Auswertung einer Vorwahl für eine Verkaufsart. */
export type VorwahlWert =
  | { art: 'keine' }
  | { art: 'stueck'; menge: number }
  | { art: 'gewicht'; gramm: number }
  | { art: 'abgewiesen'; meldung: string };

/**
 * Wertet eine Vorwahl für die Verkaufsart des gewählten Artikels aus (Artikelstamm maßgeblich,
 * fehlend = Stück). Leer = keine Vorwahl (bisheriges Verhalten).
 *
 * Stück: nur ganze Zahl 1 … 999. JEDE Eingabe mit Dezimaltrenner wird abgewiesen – auch
 * „1,0“ oder „3,“ (streng: eine Dezimaleingabe bei Stück ist fast immer ein Gewicht für den
 * falschen Artikel).
 * Gewicht: kg, > 0, höchstens drei Nachkommastellen, höchstens 99,999 kg → ganze Gramm über
 * `grammAusEingabe` (dieselbe Zerlegung wie im Gewichtsdialog). Eine ganze Zahl bei Gewicht ist
 * Kilogramm („2“ = 2,000 kg), nie Gramm.
 */
export function werteVorwahlAus(text: string, verkaufsart: Verkaufsart | undefined): VorwahlWert {
  const t = normalisiereVorwahl(text);
  if (t === '') {
    return { art: 'keine' };
  }
  if (t.length > VORWAHL_MAX_ZEICHEN) {
    return { art: 'abgewiesen', meldung: VORWAHL_MELDUNG.zuLang };
  }
  if (t.startsWith('-')) {
    return { art: 'abgewiesen', meldung: VORWAHL_MELDUNG.negativ };
  }
  const treffer = /^(\d*)(?:,(\d*))?$/.exec(t);
  if (treffer === null) {
    return { art: 'abgewiesen', meldung: VORWAHL_MELDUNG.ungueltig };
  }
  const ganz = treffer[1] ?? '';
  const nachkomma = treffer[2];
  if (verkaufsartVon(verkaufsart) === 'stueck') {
    if (nachkomma !== undefined) {
      return { art: 'abgewiesen', meldung: VORWAHL_MELDUNG.stueckNurGanz };
    }
    const ohneNullen = ganz.replace(/^0+/, '');
    if (ohneNullen === '') {
      return { art: 'abgewiesen', meldung: VORWAHL_MELDUNG.stueckMindestens };
    }
    // Länge zuerst: lange Ziffernfolgen nie über Number (keine Rundung, kein Exponent)
    if (ohneNullen.length > String(MAX_VORGABE_MENGE).length) {
      return { art: 'abgewiesen', meldung: VORWAHL_MELDUNG.stueckHoechstens };
    }
    const menge = Number(ohneNullen);
    if (menge > MAX_VORGABE_MENGE) {
      return { art: 'abgewiesen', meldung: VORWAHL_MELDUNG.stueckHoechstens };
    }
    return { art: 'stueck', menge };
  }
  const nk = nachkomma ?? '';
  if (ganz === '' && nk === '') {
    return { art: 'abgewiesen', meldung: VORWAHL_MELDUNG.gewichtFehlt };
  }
  if (nk.length > 3) {
    return { art: 'abgewiesen', meldung: VORWAHL_MELDUNG.gewichtNachkomma };
  }
  const ganzOhneNullen = ganz.replace(/^0+/, '');
  if (ganzOhneNullen === '' && /^0*$/.test(nk)) {
    return { art: 'abgewiesen', meldung: VORWAHL_MELDUNG.gewichtNull };
  }
  if (ganzOhneNullen.length > 2) {
    return { art: 'abgewiesen', meldung: VORWAHL_MELDUNG.gewichtHoechstens };
  }
  const gramm = grammAusEingabe(t);
  if (gramm === null || gramm < 1 || gramm > GEWICHT_MAX_GRAMM) {
    // nach den Prüfungen oben nicht erreichbar – Schutz, falls sich grammAusEingabe ändert
    return { art: 'abgewiesen', meldung: VORWAHL_MELDUNG.ungueltig };
  }
  return { art: 'gewicht', gramm };
}

// ---------------------------------------------------------------------------------------------
// Artikelwahl mit Vorwahl
// ---------------------------------------------------------------------------------------------

/** Auswahl wie bei `waehleArtikel`, dazu „abgewiesen“ für eine unpassende Vorwahl. */
export type VorwahlAuswahl = Auswahl | { art: 'abgewiesen'; meldung: string };

/** Ergebnis einer Artikelwahl mit Vorwahl. */
export interface VorwahlErgebnis {
  auswahl: VorwahlAuswahl;
  /** true = die Vorwahl ist für diesen Artikel verbraucht und muss geleert werden. */
  verbraucht: boolean;
}

/**
 * Artikelwahl (Kachel, Suche, Scan) mit Vorwahl – rein, ohne Zustand.
 *
 * - Gewichtsdialog offen: wie bisher verworfen, Vorwahl unverbraucht.
 * - Keine Vorwahl: bisheriges Verhalten (Stück mit `mengeOhneVorwahl`, Wiegeartikel →
 *   Gewichtsdialog-Anfrage).
 * - Gültige Vorwahl: Stück mit dieser Menge bzw. Wiegeposition direkt (kein Dialog);
 *   verbraucht.
 * - Ungültige Vorwahl: „abgewiesen“, Vorwahl unverbraucht, kein Artikel.
 */
export function waehleArtikelMitVorwahl(
  offen: WiegeAnfrage | null,
  k: Schnellwahl,
  vorwahl: string,
  ausserHaus: boolean,
  mengeOhneVorwahl = 1,
): VorwahlErgebnis {
  if (offen !== null) {
    return {
      auswahl: waehleArtikel(offen, k, () => positionMitMenge(k, 1), ausserHaus),
      verbraucht: false,
    };
  }
  const wert = werteVorwahlAus(vorwahl, k.verkaufsart);
  switch (wert.art) {
    case 'abgewiesen':
      return { auswahl: wert, verbraucht: false };
    case 'keine': {
      // kein stiller Rückfall auf 1: eine ungültige Vorgabe des Aufrufers wird abgewiesen
      if (
        !Number.isInteger(mengeOhneVorwahl) ||
        mengeOhneVorwahl < 1 ||
        mengeOhneVorwahl > MAX_VORGABE_MENGE
      ) {
        return {
          auswahl: { art: 'abgewiesen', meldung: VORWAHL_MELDUNG.mengeUngueltig },
          verbraucht: false,
        };
      }
      return {
        auswahl: waehleArtikel(
          null,
          k,
          () => positionMitMenge(k, mengeOhneVorwahl, ausserHaus),
          ausserHaus,
        ),
        verbraucht: false,
      };
    }
    case 'stueck': {
      const auswahl = waehleArtikel(
        null,
        k,
        () => positionMitMenge(k, wert.menge, ausserHaus),
        ausserHaus,
      );
      return { auswahl, verbraucht: auswahl.art === 'bonieren' };
    }
    case 'gewicht': {
      const auswahl = waehleArtikel(
        null,
        k,
        () => {
          throw new Error('Gewichtsvorwahl darf keine Stückposition ergeben');
        },
        ausserHaus,
        { gramm: wert.gramm, herkunft: 'vorwahl' },
      );
      return { auswahl, verbraucht: auswahl.art === 'bonieren' };
    }
  }
}

/**
 * Zustand der Vorwahl außerhalb von React (wie die Ref-Sperre des Gewichtsdialogs): Lesen,
 * Auswerten und Verbrauchen geschehen synchron in {@link VorwahlZustand.waehle}. Zwei schnelle
 * Wahlen hintereinander (Doppeltipp, Kachel + Scan) können dieselbe Vorwahl deshalb nie zweimal
 * verbrauchen – die zweite sieht bereits die leere Vorwahl.
 *
 * Für React: `useSyncExternalStore(z.abonniere, z.wert)`.
 */
export class VorwahlZustand {
  private text = '';
  private readonly beobachter = new Set<() => void>();

  /** Aktuelle (normalisierte) Vorwahl; '' = keine. */
  readonly wert = (): string => this.text;

  /** Änderungen beobachten; liefert die Abmeldung. */
  readonly abonniere = (fn: () => void): (() => void) => {
    this.beobachter.add(fn);
    return () => {
      this.beobachter.delete(fn);
    };
  };

  /** Taste (Touch oder Tastatur) anwenden; liefert die neue Vorwahl. */
  taste(t: VorwahlTaste): string {
    return this.setze(vorwahlTaste(this.text, t));
  }

  /**
   * Taste mit sofortiger Prüfung (O2, {@link vorwahlTasteGeprueft}): eine vierte Nachkommastelle
   * wird nicht übernommen, die Vorwahl bleibt und es gibt eine Meldung.
   */
  tippe(t: VorwahlTaste): { text: string; meldung: string | null } {
    const r = vorwahlTasteGeprueft(this.text, t);
    return { text: this.setze(r.text), meldung: r.meldung };
  }

  /**
   * Vorwahl setzen (normalisiert). Bewusst NICHT abgeschnitten: aus „00000001000“ darf nie
   * „00000001“ (= 1 Stück) werden – ein zu langer Wert wird bei der Auswertung abgewiesen.
   */
  setze(text: string): string {
    const neu = normalisiereVorwahl(text);
    if (neu !== this.text) {
      this.text = neu;
      for (const fn of this.beobachter) {
        fn();
      }
    }
    return this.text;
  }

  /** Vorwahl leeren. */
  leeren(): void {
    this.setze('');
  }

  /** Läuft gerade ein Auswahlabschluss (Schutz gegen erneuten Eintritt aus `uebernehmen`). */
  private inArbeit = false;

  /**
   * Gemeinsamer Auswahlabschluss für Kachel, Suche, Scan und Scan-Auswahl – synchron in dieser
   * Reihenfolge:
   * 1. `scannerLeeren` (`() => erkennung.abschliessen()`): die laufende Folge der ScanErkennung
   *    mit ihrer eigenen Entscheidung abschließen – gepufferte menschliche Ziffern kommen zuerst
   *    in die Vorwahl („3“ getippt, sofort Kachel → 3 Stück, nicht 1 Stück und eine
   *    liegengebliebene „3“ für den nächsten Artikel), scanverdächtige Folgen bleiben verworfen.
   * 2. Ergebnis prüfen (`waehleArtikelMitVorwahl`): abgewiesen/ignoriert/Dialog → Vorwahl bleibt.
   * 3. Position übernehmen (`uebernehmen`, Pflicht, z. B. `hinzufuegen`; false = nicht übernommen, etwa
   *    ohne Bedieneranmeldung → Vorwahl bleibt, Ergebnis „ignoriert“).
   * 4. Erst dann die Vorwahl verbrauchen.
   *
   * Ein erneuter Aufruf während eines laufenden Abschlusses wird verworfen.
   */
  waehle(
    offen: WiegeAnfrage | null,
    k: Schnellwahl,
    ausserHaus: boolean,
    optionen: VorwahlWahlOptionen,
  ): VorwahlErgebnis {
    if (this.inArbeit) {
      return {
        auswahl: { art: 'ignoriert', grund: 'Artikelwahl läuft bereits' },
        verbraucht: false,
      };
    }
    this.inArbeit = true;
    try {
      optionen.scannerLeeren?.();
      const ergebnis = waehleArtikelMitVorwahl(
        offen,
        k,
        this.text,
        ausserHaus,
        optionen.mengeOhneVorwahl ?? 1,
      );
      if (ergebnis.auswahl.art === 'bonieren' && !optionen.uebernehmen(ergebnis.auswahl.position)) {
        return {
          auswahl: { art: 'ignoriert', grund: VORWAHL_MELDUNG.nichtUebernommen },
          verbraucht: false,
        };
      }
      if (ergebnis.verbraucht) {
        this.leeren();
      }
      return ergebnis;
    } finally {
      this.inArbeit = false;
    }
  }

  /** Ereignis des Verkaufs anwenden ({@link vorwahlNachEreignis}); liefert die Entscheidung. */
  ereignis(e: VorwahlEreignis): 'loeschen' | 'erhalten' {
    const entscheidung = vorwahlNachEreignis(e);
    if (entscheidung === 'loeschen') {
      this.leeren();
    }
    return entscheidung;
  }
}

/** Optionen des Auswahlabschlusses {@link VorwahlZustand.waehle}. */
export interface VorwahlWahlOptionen {
  /** Stückzahl ohne Vorwahl (Standard 1; ungültig → abgewiesen, kein Rückfall). */
  mengeOhneVorwahl?: number;
  /**
   * Laufende Folge der ScanErkennung vorher abschließen: `() => erkennung.abschliessen()` –
   * dieselbe Entscheidung wie nach Ablauf der Haltezeit (kurze menschliche Eingabe → frei,
   * scannerschnelle Folge ab `minLaenge` bzw. Präfix-Scan → verworfen). NICHT `freigeben()`:
   * das gäbe auch scanverdächtige Folgen frei.
   */
  scannerLeeren?: () => void;
  /**
   * Position übernehmen (Pflicht); false = nicht übernommen (Vorwahl bleibt). Die Vorwahl wird
   * nur nach bestätigter Übernahme verbraucht.
   */
  uebernehmen: (position: Position) => boolean;
}

// ---------------------------------------------------------------------------------------------
// Lebenszyklus
// ---------------------------------------------------------------------------------------------

/** Ereignisse des Verkaufs, die die Vorwahl betreffen. */
export type VorwahlEreignis =
  // löschen
  | 'uebernommen'
  | 'loeschtaste'
  | 'bonGeleert'
  | 'geparkt'
  | 'bonUebernommen'
  | 'zahlungGestartet'
  | 'bedienerWechsel'
  | 'trainingWechsel'
  | 'reiterWechsel'
  | 'neuerVerkauf'
  // erhalten
  | 'validierungsfehler'
  | 'abgebrochen'
  | 'scanUnbekannt'
  | 'scanGesperrt'
  | 'scanVerworfen'
  | 'mehrfachtrefferAbgebrochen'
  | 'nichtAngemeldet'
  | 'eingeklappt'
  | 'dialogOffen';

/**
 * Lebenszyklus-Tabelle (rein): Löschen bei Erfolg, Löschtaste (C/X), Bon leeren, Parken,
 * Bonübernahme (Tisch/Wiederaufnahme), Zahlungsstart, Bediener-, Trainings- oder Reiterwechsel
 * und neuem Verkauf. Erhalten bei Validierungsfehler, Abbruch, unbekanntem, gesperrtem oder
 * verworfenem Scan, abgebrochener Mehrfachtrefferwahl, fehlender Anmeldung, Einklappen des
 * Panels und offenem Dialog.
 */
export const VORWAHL_LEBENSZYKLUS: Readonly<Record<VorwahlEreignis, 'loeschen' | 'erhalten'>> = {
  uebernommen: 'loeschen',
  loeschtaste: 'loeschen',
  bonGeleert: 'loeschen',
  geparkt: 'loeschen',
  bonUebernommen: 'loeschen',
  zahlungGestartet: 'loeschen',
  bedienerWechsel: 'loeschen',
  trainingWechsel: 'loeschen',
  reiterWechsel: 'loeschen',
  neuerVerkauf: 'loeschen',
  validierungsfehler: 'erhalten',
  abgebrochen: 'erhalten',
  scanUnbekannt: 'erhalten',
  scanGesperrt: 'erhalten',
  scanVerworfen: 'erhalten',
  mehrfachtrefferAbgebrochen: 'erhalten',
  nichtAngemeldet: 'erhalten',
  eingeklappt: 'erhalten',
  dialogOffen: 'erhalten',
};

/** Was mit der Vorwahl bei einem Ereignis geschieht (unbekanntes Ereignis → erhalten). */
export function vorwahlNachEreignis(e: VorwahlEreignis): 'loeschen' | 'erhalten' {
  return Object.hasOwn(VORWAHL_LEBENSZYKLUS, e) ? VORWAHL_LEBENSZYKLUS[e] : 'erhalten';
}

// ---------------------------------------------------------------------------------------------
// Physische Tastatur
// ---------------------------------------------------------------------------------------------

/**
 * Was die vorhandene Scanner-Erkennung (`ScanErkennung.taste` in `scanner.ts`) mit einer Taste
 * gemacht hat: `'gehalten'` = gehört (vorerst) zu einem Scan, `'frei'` = normale Taste,
 * `'freigegeben'` = zurückgehaltene Taste, die die Erkennung per `onFreigabe` als menschliche
 * Eingabe weitergereicht hat. Eine eigene Scanner-Erkennung gibt es hier nicht.
 */
export type ScanAnspruch = 'gehalten' | 'frei' | 'freigegeben';

/** Felder eines Tastaturereignisses, die der Filter liest (KeyboardEvent passt). */
export interface VorwahlTastenEreignis {
  key: string;
  altKey?: boolean;
  ctrlKey?: boolean;
  metaKey?: boolean;
  isComposing?: boolean;
  defaultPrevented?: boolean;
  target?: unknown;
}

/**
 * Elemente, die Tasten selbst verarbeiten: Eingabefelder, editierbare Bereiche, Felder mit
 * Scansperre und der Ziffernblock „Menge oder Betrag“ (eigene Tastaturbehandlung).
 */
const EIGENE_TASTEN = [
  'input',
  'textarea',
  'select',
  '[contenteditable]:not([contenteditable="false"])',
  '[data-scansperre]',
  '.ziffernblock',
].join(', ');

/** Nimmt dieses Element (bzw. ein Vorfahr) Tasten selbst an? Ohne DOM-Abhängigkeit geprüft. */
export function istEingabeZiel(ziel: unknown): boolean {
  if (typeof ziel !== 'object' || ziel === null) {
    return false;
  }
  const el = ziel as { tagName?: unknown; isContentEditable?: unknown; closest?: unknown };
  const tag = typeof el.tagName === 'string' ? el.tagName.toUpperCase() : '';
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || el.isContentEditable === true) {
    return true;
  }
  if (typeof el.closest === 'function') {
    const treffer: unknown = (el.closest as (s: string) => unknown).call(ziel, EIGENE_TASTEN);
    return treffer !== null && treffer !== undefined;
  }
  return false;
}

/**
 * Gehört dieser Tastendruck in die Vorwahl? Liefert die Vorwahltaste oder null.
 *
 * Nur wenn: die Scanner-Erkennung die Taste nicht beansprucht (`scan !== 'gehalten'`, auch
 * nicht bereits per `preventDefault`), keine Modifikatortaste und keine IME-Komposition, weder
 * Ereignisziel noch fokussiertes Element ein Eingabefeld ({@link istEingabeZiel}) und die
 * Oberfläche die Vorwahl nicht sperrt (offener Dialog, Zahlung …).
 *
 * Tasten: Ziffern, `,` und `.` (Dezimaltrenner), Rücktaste (⌫), Entf (Löschen). Esc gehört
 * bewusst nicht dazu – es schließt in der Kasse Dialoge (R5).
 */
export function vorwahlTasteAusTastatur(
  e: VorwahlTastenEreignis,
  umgebung: { scan: ScanAnspruch; fokus?: unknown; gesperrt?: boolean },
): VorwahlTaste | null {
  if (
    umgebung.scan === 'gehalten' ||
    umgebung.gesperrt === true ||
    e.defaultPrevented === true ||
    e.altKey === true ||
    e.ctrlKey === true ||
    e.metaKey === true ||
    e.isComposing === true ||
    istEingabeZiel(e.target) ||
    istEingabeZiel(umgebung.fokus)
  ) {
    return null;
  }
  if (e.key === 'Backspace') {
    return '⌫';
  }
  if (e.key === 'Delete') {
    return 'C';
  }
  return vorwahlTasteAusZeichen(e.key);
}

/**
 * Fertiger Tastatur-Listener für die Vorwahl (Bubble-Phase am Fenster), damit die UI nur noch
 * einhängt. Erwartet, dass die vorhandene `ScanErkennung` vorher in der Capture-Phase läuft
 * (dort werden gehaltene Tasten mit `preventDefault` + `stopPropagation` geschluckt) und ihre
 * per `onFreigabe` nachgereichten Ereignisse markiert (`weitergeleitet`, in `Verkauf.tsx` das
 * `WeakSet` `WEITERGELEITET`).
 *
 * Eine angenommene Taste wird mit `preventDefault()` beansprucht – nachfolgende Listener, die
 * `defaultPrevented` beachten (der heutige Ziffern-Kürzel-Listener), verarbeiten sie nicht
 * zusätzlich. Liefert true, wenn die Taste in die Vorwahl ging.
 */
export function vorwahlTastenListener(
  zustand: VorwahlZustand,
  optionen: {
    weitergeleitet: (e: Event) => boolean;
    gesperrt?: () => boolean;
    fokus?: () => unknown;
    /**
     * Umleitung (z. B. aktiver Betragseditor „Als Betrag buchen“): liefert ein Ziel, bekommt die
     * Taste dieses statt der Vorwahl. null = Vorwahl.
     */
    umleiten?: () => ((t: VorwahlTaste) => void) | null;
    /** Meldung der sofortigen Prüfung (O2: vierte Nachkommastelle); null = keine. */
    onMeldung?: (meldung: string | null) => void;
  },
): (e: KeyboardEvent) => boolean {
  return (e) => {
    const taste = vorwahlTasteAusTastatur(e, {
      scan: optionen.weitergeleitet(e) ? 'freigegeben' : 'frei',
      fokus: optionen.fokus?.(),
      gesperrt: optionen.gesperrt?.() ?? false,
    });
    if (taste === null) {
      return false;
    }
    e.preventDefault();
    const ziel = optionen.umleiten?.() ?? null;
    if (ziel !== null) {
      ziel(taste);
      return true;
    }
    const r = zustand.tippe(taste);
    optionen.onMeldung?.(r.meldung);
    return true;
  };
}

// ---------------------------------------------------------------------------------------------
// Panel-Zustand (ein/aus) je Gerät
// ---------------------------------------------------------------------------------------------

/** Schlüssel im localStorage. */
export const VORWAHL_PANEL_SCHLUESSEL = 'bilanx.vorwahl.panel';

/** Ab dieser Fensterbreite ist das Panel ohne gespeicherte Wahl offen. */
export const VORWAHL_PANEL_AB_BREITE = 1280;

/** Ausschnitt von `Storage`, den das Panel braucht. */
export interface PanelSpeicher {
  getItem(schluessel: string): string | null;
  setItem(schluessel: string, wert: string): void;
}

/**
 * localStorage des Fensters oder null – schon der Zugriff auf `localStorage` kann werfen
 * (gesperrte Website-Daten, Sandbox).
 */
export function standardPanelSpeicher(): PanelSpeicher | null {
  try {
    const s = (globalThis as { localStorage?: PanelSpeicher }).localStorage;
    return s ?? null;
  } catch {
    return null;
  }
}

/** Voreinstellung ohne gespeicherte Wahl: offen ab {@link VORWAHL_PANEL_AB_BREITE} px. */
export function vorwahlPanelStandard(breitePx: number): boolean {
  return Number.isFinite(breitePx) && breitePx >= VORWAHL_PANEL_AB_BREITE;
}

/**
 * Gespeicherter Panel-Zustand; fehlend, unlesbar oder unbekannter Wert → Voreinstellung nach
 * Breite. Wirft nie.
 */
export function ladeVorwahlPanel(
  speicher: PanelSpeicher | null | undefined,
  breitePx: number,
): boolean {
  try {
    const wert = speicher?.getItem(VORWAHL_PANEL_SCHLUESSEL) ?? null;
    if (wert === '1') {
      return true;
    }
    if (wert === '0') {
      return false;
    }
  } catch {
    // unlesbar → Voreinstellung
  }
  return vorwahlPanelStandard(breitePx);
}

/** Panel-Zustand speichern; false, wenn nicht gespeichert werden konnte. Wirft nie. */
export function speichereVorwahlPanel(
  speicher: PanelSpeicher | null | undefined,
  offen: boolean,
): boolean {
  if (speicher === null || speicher === undefined) {
    return false;
  }
  try {
    speicher.setItem(VORWAHL_PANEL_SCHLUESSEL, offen ? '1' : '0');
    return true;
  } catch {
    return false;
  }
}

// ---------------------------------------------------------------------------------------------
// Oberflächenhilfen (Vorwahl V1, Figma `430:19849`, freigegeben 2026-11-04)
// ---------------------------------------------------------------------------------------------

/**
 * O2: Taste mit sofortiger Prüfung. Eine vierte Nachkommastelle wird sofort abgewiesen
 * (VW-6c: „0,755“ + „5“ bleibt „0,755“) – mit Meldung, nie still. Alle anderen Tasten wie
 * {@link vorwahlTaste}; ungültige Werte (zu lang, zwei Trenner) meldet weiter die Auswertung.
 */
export function vorwahlTasteGeprueft(
  text: string,
  taste: VorwahlTaste,
): { text: string; meldung: string | null } {
  const alt = normalisiereVorwahl(text);
  const neu = vorwahlTaste(alt, taste);
  const komma = neu.lastIndexOf(TRENNER);
  if (/^\d$/.test(taste) && komma >= 0 && neu.length - komma - 1 > 3 && neu !== alt) {
    return { text: alt, meldung: VORWAHL_MELDUNG.gewichtNachkomma };
  }
  return { text: neu, meldung: null };
}

/** Anzeige einer Vorwahl im Hinweis über den Kacheln und im Panel (neutral, Codex-Fund 12). */
export interface VorwahlBeschreibung {
  /** Kurzform für „Nächster Artikel: …“, z. B. „3 Stück / 3,000 kg“ oder „0,750 kg“. */
  kurz: string;
  /** Zeile unter der Anzeige im Panel: wofür der Wert gilt. */
  einheit: string;
}

/**
 * Beschreibung einer Vorwahl, bevor der Artikel feststeht. Die Einheit bestimmt erst der Artikel
 * (`verkaufsart`) – deshalb zeigt eine ganze Zahl beide Deutungen (O1: bei Gewicht kg), eine Zahl
 * mit Komma nur das Gewicht. Ungültige Werte erscheinen unverändert; die Meldung kommt erst bei
 * der Artikelwahl (Fehler nie still). Leer → null (kein Hinweis).
 */
export function vorwahlBeschreibung(text: string): VorwahlBeschreibung | null {
  const t = normalisiereVorwahl(text);
  if (t === '') {
    return null;
  }
  const s = werteVorwahlAus(t, 'stueck');
  const g = werteVorwahlAus(t, 'gewicht');
  const kg = g.art === 'gewicht' ? formatiereKg(g.gramm) : null;
  if (s.art === 'stueck' && kg !== null) {
    return {
      kurz: `${String(s.menge)} Stück / ${kg}`,
      einheit: `Stückartikel: ${String(s.menge)} Stück · Gewichtsartikel: ${kg}`,
    };
  }
  if (kg !== null) {
    return { kurz: kg, einheit: `nur für Gewichtsartikel: ${kg}` };
  }
  if (s.art === 'stueck') {
    return {
      kurz: `${String(s.menge)} Stück`,
      einheit: `nur für Stückartikel: ${String(s.menge)} Stück`,
    };
  }
  return { kurz: t, einheit: 'Ungültige Vorwahl – korrigieren oder mit C löschen' };
}

// ---------------------------------------------------------------------------------------------
// „Als Betrag buchen“ (O3): Euro mit Komma, exakt in Ganzzahl-Cent
// ---------------------------------------------------------------------------------------------

/** Höchstbetrag der freien Zeile in Cent (wie der Ziffernblock, `ziffern.ts`). */
export const BETRAG_MAX_CENT = 999_999_999;

/** Meldungen des Betragswegs. */
export const BETRAG_MELDUNG = {
  fehlt: 'Betrag fehlt – Ziffern eingeben',
  null: 'Betrag muss größer als 0 € sein',
  negativ: 'Keine negativen Beträge',
  nachkomma: 'Betrag – höchstens 2 Nachkommastellen',
  hoechstens: 'Betrag höchstens 9.999.999,99 €',
  ungueltig: 'Ungültiger Betrag – nur Ziffern und ein Komma',
} as const;

/**
 * Betragstext (Euro, Komma oder Punkt) → ganze Cent, exakt aus den Ziffern zerlegt (nie
 * `parseFloat`, keine Rundung): „12,5“ → 1250, „0,07“ → 7, „12,505“ → abgewiesen.
 */
export function betragCentAusText(text: string): { cent: number } | { meldung: string } {
  const t = normalisiereVorwahl(text);
  if (t === '') {
    return { meldung: BETRAG_MELDUNG.fehlt };
  }
  if (t.startsWith('-')) {
    return { meldung: BETRAG_MELDUNG.negativ };
  }
  const treffer = /^(\d*)(?:,(\d*))?$/.exec(t);
  if (treffer === null) {
    return { meldung: BETRAG_MELDUNG.ungueltig };
  }
  const ganz = (treffer[1] ?? '').replace(/^0+/, '');
  const nachkomma = treffer[2] ?? '';
  if (nachkomma.length > 2) {
    return { meldung: BETRAG_MELDUNG.nachkomma };
  }
  if ((treffer[1] ?? '') === '' && nachkomma === '') {
    return { meldung: BETRAG_MELDUNG.fehlt };
  }
  // Länge zuerst: lange Ziffernfolgen nie über Number (kein Exponent, keine Rundung)
  if (ganz.length > String(Math.floor(BETRAG_MAX_CENT / 100)).length) {
    return { meldung: BETRAG_MELDUNG.hoechstens };
  }
  const cent = Number(ganz === '' ? '0' : ganz) * 100 + Number(nachkomma.padEnd(2, '0'));
  if (cent > BETRAG_MAX_CENT) {
    return { meldung: BETRAG_MELDUNG.hoechstens };
  }
  if (cent === 0) {
    return { meldung: BETRAG_MELDUNG.null };
  }
  return { cent };
}

/**
 * Taste im Betragseditor: Ziffern anhängen, Komma (einmal), ⌫, C. Eine dritte Nachkommastelle,
 * ein zweites Komma und ein Überschreiten des Höchstbetrags werden abgewiesen – die Eingabe bleibt
 * stehen, es gibt eine Meldung (O3).
 */
export function betragTaste(
  text: string,
  taste: VorwahlTaste,
): { text: string; meldung: string | null } {
  const t = normalisiereVorwahl(text);
  if (taste === 'C') {
    return { text: '', meldung: null };
  }
  if (taste === '⌫') {
    return { text: t.slice(0, -1), meldung: null };
  }
  if (taste === TRENNER) {
    if (t.includes(TRENNER)) {
      return { text: t, meldung: BETRAG_MELDUNG.ungueltig };
    }
    return { text: t === '' ? `0${TRENNER}` : `${t}${TRENNER}`, meldung: null };
  }
  const neu = t === '0' ? taste : `${t}${taste}`;
  const r = betragCentAusText(neu);
  if (
    'meldung' in r &&
    (r.meldung === BETRAG_MELDUNG.nachkomma || r.meldung === BETRAG_MELDUNG.hoechstens)
  ) {
    return { text: t, meldung: r.meldung };
  }
  return { text: neu, meldung: null };
}
